// src/pages/Pos.js

import React, { useState, useEffect, useMemo } from 'react';
import { 
    Container, 
    Typography, 
    Button, 
    Grid, 
    Paper, 
    Table, 
    TableBody, 
    TableCell, 
    TableContainer, 
    TableHead, 
    TableRow,
    TextField,
    IconButton,
    Box,
    Divider,
    Chip,
    Card,
    CardContent,
    Tabs,
    InputAdornment,
    Tab,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    CircularProgress,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    Autocomplete,
    Tooltip,
    useTheme,
    Alert,
    Snackbar,
    Avatar,
    Input,
    InputBase
} from '@mui/material';
import { 
    Add, Remove, Delete, ShoppingCart, Search, Person,
    Phone, Money, MoneyOffRounded, Close, FilterList,
    Category as CategoryIcon, Close as CloseIcon, PersonAdd,
    Print, Receipt, CheckCircle, Cancel, Inventory, Edit,
    Scale
} from '@mui/icons-material';
import useCustomToast from '../../hooks/useToast';
import {
    APIOrdersStore,
    APICategoriesList,
    APIProductsList,
    APICustomerList,
    APIUnitName
} from '../../apiServices/APIs';
import { recordsCount, getUserDetails } from '../../utils/helpers';
import { apiService } from '../../apiServices/ApiService';
import { usePermission } from '../../hooks/usePermission';
import LoadingState from '../../components/LoadingState';
import ErrorState from '../../components/ErrorrState';
import PrintReceipt from './PrintReceipt';
import { useNavigate } from 'react-router-dom';

const Pos = () => {
    const [loading, setLoading] = useState(false);
    const navigate = useNavigate();
    const [processingOrder, setProcessingOrder] = useState(false);
    const [error, setError] = useState(null);
    const theme = useTheme();
    const showToast = useCustomToast();
    const [pageState, setPageState] = useState({
        productsData: [],
        categoriesData: [],
        customersData: [],
        unitsData: [],
        posStats: {
            total_products: 0,
            total_customers: 0,
            today_orders: 0,
            today_revenue: 0
        }
    });
    const [cart, setCart] = useState([]);
    const [searchTerm, setSearchTerm] = useState('');
    const [selectedCategory, setSelectedCategory] = useState('');
    const [editingPriceId, setEditingPriceId] = useState(null);
    const [editPriceValue, setEditPriceValue] = useState('');
    
    // Search input states for autocomplete
    const [customerSearchInput, setCustomerSearchInput] = useState('');
    
    // Form states
    const [formData, setFormData] = useState({
        customerName: '',
        customerPhone: '',
        customerGivenCash: '',
        orderType: 'direct_payment',
        paymentMethod: 'cash',
        orderStatus: 'completed',
        orderNotes: ''
    });
    
    const [errors, setErrors] = useState({
        customerName: false,
        customerPhone: false,
        customerGivenCash: false
    });

    const [orderSettingsTab, setOrderSettingsTab] = useState(0);
    const [customerEditable, setCustomerEditable] = useState(true);
    const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'info' });

    const { hasPermission, loading: permissionLoading } = usePermission();

    // Determine if payment fields should be shown
    const isQuotation = formData.orderStatus === 'quotation';
    const isLoanOrder = formData.orderType === 'loan_order';

    useEffect(() => {
        apiService.setToastFunction(showToast);
        fetchData();
    }, [showToast]);

    const fetchData = async () => {
        setLoading(true);
        setError(null);

        try {
            const userBranchId = getUserDetails("branchId");

            const [productsResult, categoriesResult, customersResult, unitsResult] = await Promise.all([
                apiService.list(userBranchId, APIProductsList),
                apiService.list(userBranchId, APICategoriesList),
                apiService.list(userBranchId, APICustomerList),
                apiService.list(userBranchId, APIUnitName)
            ]);

            if (productsResult.success && categoriesResult.success && customersResult.success) {
                const products = Array.isArray(productsResult.data)
                    ? productsResult.data
                        .filter(product => product.status === 1 && product.stock > 0)
                         : [];

                const categories = Array.isArray(categoriesResult.data) 
                    ? categoriesResult.data 
                    : [];

                const customers = Array.isArray(customersResult.data) 
                    ? customersResult.data 
                    : [];

                const units = Array.isArray(unitsResult.data) 
                    ? unitsResult.data 
                    : [];

                setPageState(prev => ({
                    ...prev,
                    productsData: products,
                    categoriesData: categories,
                    customersData: customers,
                    unitsData: units,
                    posStats: {
                        total_products: products.length,
                        total_customers: customers.length,
                        today_orders: 0,
                        today_revenue: 0
                    }
                }));
            } else {
                setError(productsResult.message || categoriesResult.message || customersResult.message);
            }
        } catch (error) {
            setError("Failed to load data");
            console.error("Fetch error:", error);
        } finally {
            setLoading(false);
        }
    };

    // Get unit name for a product
    const getUnitName = (product) => {
        if (product && product.unit_id) {
            const unit = pageState.unitsData.find(u => u.id === product.unit_id);
            return unit ? `${unit.name} (${unit.code})` : 'N/A';
        }
        return 'N/A';
    };

    // Get unit code for a product
    const getUnitCode = (product) => {
        if (product && product.unit_id) {
            const unit = pageState.unitsData.find(u => u.id === product.unit_id);
            return unit ? unit.code : 'N/A';
        }
        return 'N/A';
    };

    const filteredProducts = useMemo(() => {
        return pageState.productsData.filter(product => {
            const unitName = getUnitName(product);
            
            const matchesSearch = searchTerm === '' || 
                product.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                product.category_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                unitName.toLowerCase().includes(searchTerm.toLowerCase());
            
            const matchesCategory = selectedCategory === '' || 
                String(product.category_id) === String(selectedCategory);
            
            return matchesSearch && matchesCategory;
        });
    }, [pageState.productsData, searchTerm, selectedCategory, pageState.unitsData]);

    const formatCurrency = (amount) => {
        return new Intl.NumberFormat('en-UG', {
            style: 'currency',
            currency: 'UGX',
            minimumFractionDigits: 0,
            maximumFractionDigits: 0
        }).format(amount || 0);
    };

    const handleInputChange = (e) => {
        const { name, value } = e.target;

        setErrors(prev => ({
            ...prev,
            [name]: false
        }));

        if (name === 'customerGivenCash' || name === 'customerPhone') {
            if (value === '' || /^\d+$/.test(value)) {
                setFormData(prev => ({
                    ...prev,
                    [name]: value
                }));
            }
        } else if (name === 'orderStatus') {
            // When changing to quotation, clear the cash given amount
            setFormData(prev => ({
                ...prev,
                [name]: value,
                ...(value === 'quotation' ? { customerGivenCash: '' } : {})
            }));
        } else {
            setFormData(prev => ({
                ...prev,
                [name]: value
            }));
        }
    };

    const handleCustomerSelect = (customer) => {
        if (customer) {
            setFormData(prev => ({
                ...prev,
                customerName: customer.name,
                customerPhone: customer.phone || ''
            }));
            setCustomerEditable(false);
        } else {
            setCustomerEditable(true);
        }
    };

    const handleClearCustomer = () => {
        setFormData(prev => ({
            ...prev,
            customerName: '',
            customerPhone: ''
        }));
        setCustomerEditable(true);
    };

    const handleCreateNewCustomer = () => {
        navigate('/customer');
    };

    const addToCart = (product) => {
        const existingItem = cart.find(item => item.id === product.id);
        if (existingItem) {
            if (existingItem.quantity < product.stock) {
                setCart(cart.map(item =>
                    item.id === product.id
                        ? { ...item, quantity: item.quantity + 1 }
                        : item
                ));
            } else {
                showToast("Stock Limit", "Cannot add more than available stock", "warning");
            }
        } else {
            setCart([...cart, { 
                ...product, 
                quantity: 1,
                price: product.selling,
                originalPrice: product.selling,
                unit_code: getUnitCode(product),
                unit_name: getUnitName(product)
            }]);
        }
    };

    const removeFromCart = (productId) => {
        setCart(cart.filter(item => item.id !== productId));
    };

    const adjustQuantity = (productId, adjustment) => {
        setCart(cart.map(item => {
            if (item.id === productId) {
                const newQuantity = item.quantity + adjustment;
                const product = pageState.productsData.find(p => p.id === productId);
                if (newQuantity > 0 && newQuantity <= (product?.stock || 0)) {
                    return { ...item, quantity: newQuantity };
                } else if (newQuantity > (product?.stock || 0)) {
                    showToast("Stock Limit", "Cannot add more than available stock", "warning");
                }
            }
            return item;
        }));
    };

    const startEditingPrice = (item) => {
        setEditingPriceId(item.id);
        setEditPriceValue(item.price.toString());
    };

    const saveEditedPrice = (itemId) => {
        const newPrice = parseFloat(editPriceValue);
        if (isNaN(newPrice) || newPrice < 0) {
            showToast("Invalid Price", "Please enter a valid price", "error");
            return;
        }
        
        setCart(cart.map(item =>
            item.id === itemId
                ? { ...item, price: newPrice }
                : item
        ));
        setEditingPriceId(null);
        setEditPriceValue('');
        showToast("Price Updated", `Price changed to ${formatCurrency(newPrice)}`, "success");
    };

    const cancelEditingPrice = () => {
        setEditingPriceId(null);
        setEditPriceValue('');
    };

    const handlePriceKeyDown = (e, itemId) => {
        if (e.key === 'Enter') {
            saveEditedPrice(itemId);
        } else if (e.key === 'Escape') {
            cancelEditingPrice();
        }
    };

    const calculateTotal = () => {
        return cart.reduce((total, item) => total + (item.price * item.quantity), 0);
    };

    const calculateBalance = () => {
        const total = calculateTotal();
        const givenCash = parseFloat(formData.customerGivenCash) || 0;
        return givenCash - total;
    };

    const validateOrder = () => {
        if (cart.length === 0) {
            showToast("Cart Empty", "Please add items to cart", "warning");
            return false;
        }
        
        if (!formData.customerName?.trim()) {
            showToast("Validation Error", "Customer name is required", "warning");
            return false;
        }

        // Only validate payment for direct_payment and non-quotation orders
        if (!isQuotation && formData.orderType === 'direct_payment') {
            if (!formData.customerGivenCash || parseFloat(formData.customerGivenCash) <= 0) {
                showToast("Validation Error", "Please enter customer given amount", "warning");
                return false;
            }
            if (parseFloat(formData.customerGivenCash) < calculateTotal()) {
                showToast("Payment Error", "Customer given amount is less than total", "warning");
                return false;
            }
        }

        return true;
    };

    const handleCheckout = async () => {
        if (!validateOrder()) return;

        if (!hasPermission('ADD')) {
            showToast("Permission Denied", "You don't have permission to create orders", "error");
            return;
        }

        setProcessingOrder(true);

        const cartItems = cart.map(item => {
            const category = pageState.categoriesData.find(c => c.id === item.category_id);
            return {
                product_id: item.id,
                name: item.name,
                quantity: item.quantity,
                price: item.price,
                category: category ? category.name : 'Uncategorized',
                unit: item.unit_name || 'N/A',
                unit_code: item.unit_code || 'N/A',
                total: item.price * item.quantity
            };
        });

        const orderData = new FormData();
        orderData.append('user_id', getUserDetails("Id"));
        orderData.append('branch_id', getUserDetails("branchId"));
        orderData.append('customer_name', formData.customerName);
        orderData.append('customer_phone', formData.customerPhone);
        orderData.append('customer_given_cash', isQuotation ? 0 : parseFloat(formData.customerGivenCash || 0));
        orderData.append('total_amount', calculateTotal());
        orderData.append('order_type', formData.orderType);
        orderData.append('payment_method', isQuotation ? 'cash' : formData.paymentMethod);
        orderData.append('order_status', formData.orderStatus);
        orderData.append('order_notes', formData.orderNotes || '');
        orderData.append('items', JSON.stringify(cartItems));
        orderData.append('orderDetails', JSON.stringify(cartItems));

        try {
            const result = await apiService.create(APIOrdersStore, orderData);

            if (result && result.success) {
                showToast("Success", isQuotation ? "Quotation created successfully!" : "Order completed successfully!", "success");
                
                setCart([]);
                setFormData({
                    customerName: '',
                    customerPhone: '',
                    customerGivenCash: '',
                    orderType: 'direct_payment',
                    paymentMethod: 'cash',
                    orderStatus: 'completed',
                    orderNotes: ''
                });
                setCustomerEditable(true);
                setOrderSettingsTab(0);
                
                fetchData();
            }
        } catch (error) {
            console.error("Checkout error:", error);
            showToast("Error", error.message || "Failed to process order", "error");
        } finally {
            setProcessingOrder(false);
        }
    };

    const handleOrderSettingsTabChange = (event, newValue) => {
        setOrderSettingsTab(newValue);
    };

    const handleClearAllFilters = () => {
        setSelectedCategory('');
        setSearchTerm('');
    };

    const getCategoryName = (categoryId) => {
        const category = pageState.categoriesData.find(c => c.id === categoryId);
        return category ? category.name : 'N/A';
    };

    if (permissionLoading) {
        return <LoadingState message="Loading permissions..." variant="medical" size="large" fullPage />;
    }

    if (loading) {
        return <LoadingState message="Loading POS Data..." variant="medical" size="large" fullPage />;
    }

    if (error) {
        return <ErrorState error={error} onRetry={fetchData} title="Unable to Load POS Data" variant="medical" fullPage retryText="Retry Loading" />;
    }

    return (
        <Container maxWidth="xl" sx={{ py: 3 }}>
            {/* Header with Stats */}
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 3, justifyContent: 'space-between' }}>
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    <ShoppingCart color="primary" sx={{ fontSize: 30, mr: 2 }} />
                    <Typography sx={{ fontWeight: 'bold', color: 'primary.main', fontSize: '1.5rem' }}>
                        Point of Sale
                    </Typography>
                </Box>
                
                <Box sx={{ display: 'flex', gap: 2 }}>
                    <Chip icon={<Inventory />} label={`${recordsCount(pageState.posStats.total_products)} Products`} color="primary" variant="outlined" />
                    <Chip icon={<Person />} label={`${recordsCount(pageState.posStats.total_customers)} Customers`} color="success" variant="outlined" />
                </Box>
            </Box>

            <Grid container spacing={3} sx={{ display: 'flex', flexWrap: 'nowrap' }}>
                {/* Products List - Left Side */}
                <Grid item xs={12} md={8} sx={{ flex: '0 0 50%', minWidth: 0 }}>
                    <Paper elevation={2} sx={{ p: 3, borderRadius: 3, height: '100%', background:'#F5F5F0' }}>
                        {/* Search and Filter Section */}
                        <Grid container spacing={2} sx={{ mb: 3 }}>
                            <Grid item xs={12} sm={6}>
                                <Paper variant="outlined" sx={{ p: '4px 8px', borderRadius: '8px', display: 'flex', alignItems: 'center', backgroundColor: theme.palette.primary.light }}>
                                    <CategoryIcon sx={{ color: 'white', mr: 1 }} />
                                    <FormControl variant="standard" sx={{ minWidth: 120 }}>
                                        <Select
                                            value={selectedCategory}
                                            onChange={(e) => setSelectedCategory(e.target.value)}
                                            displayEmpty
                                            sx={{
                                                color: 'white',
                                                '&:before, &:after': { borderBottom: 'none' },
                                                '& .MuiSelect-select': { paddingRight: '24px' },
                                                '& .MuiSvgIcon-root': { color: 'white' }
                                            }}
                                            inputProps={{ 'aria-label': 'Select category' }}
                                        >
                                            <MenuItem value="">
                                                <em>All Categories</em>
                                            </MenuItem>
                                            {pageState.categoriesData.map((category) => (
                                                <MenuItem key={category.id} value={category.id.toString()}>
                                                    {category.name}
                                                </MenuItem>
                                            ))}
                                        </Select>
                                    </FormControl>
                                    {selectedCategory && (
                                        <IconButton size="small" onClick={() => setSelectedCategory('')} sx={{ color: 'white', ml: 1 }}>
                                            <Close fontSize="small" />
                                        </IconButton>
                                    )}
                                </Paper>
                            </Grid>
                            <Grid item xs={12} sm={6}>
                                <Paper sx={{ p: '2px 8px', display: 'flex', alignItems: 'center', borderRadius: '8px', backgroundColor: theme.palette.primary.light }}>
                                    <Search sx={{ mr: 1, color: 'white' }} />
                                    <TextField
                                        fullWidth
                                        variant="standard"
                                        placeholder="Search Products, Category or Unit..."
                                        value={searchTerm}
                                        onChange={(e) => setSearchTerm(e.target.value)}
                                        sx={{
                                            '& .MuiInput-underline:before': { borderBottom: 'none' },
                                            '& .MuiInput-underline:after': { borderBottom: 'none' },
                                            '& input': { color: 'white', '&::placeholder': { color: 'rgba(255, 255, 255, 0.7)', opacity: 1 } }
                                        }}
                                        size="small"
                                    />
                                </Paper>
                            </Grid>
                            {(selectedCategory || searchTerm) && (
                                <Grid item xs={12}>
                                    <Button variant="outlined" startIcon={<Close />} onClick={handleClearAllFilters} size="small" sx={{ textTransform: 'none', borderRadius: '8px' }}>
                                        Clear All Filters
                                    </Button>
                                </Grid>
                            )}
                        </Grid>

                        {/* Products Grid */}
                        <Grid container spacing={2}>
                            {filteredProducts.map((product) => {
                                const unitName = getUnitName(product);
                                return (
                                    <Grid item xs={6} sm={4} md={3} key={product.id}>
                                        <Card
                                            elevation={1}
                                            sx={{
                                                cursor: 'pointer',
                                                borderRadius: 2,
                                                transition: 'all 0.3s ease',
                                                '&:hover': { backgroundColor: 'primary.light', color: 'white', transform: 'translateY(-2px)', boxShadow: 3 }
                                            }}
                                            onClick={() => addToCart(product)}
                                        >
                                            <CardContent sx={{ p: 2 }}>
                                                <Typography variant="subtitle2" sx={{ fontWeight: 'bold', mb: 1}}>
                                                    {getCategoryName(product.category_id)}
                                                </Typography>
                                                <Typography variant="subtitle1" sx={{ fontWeight: 'bold', mb: 0.5 }}>
                                                    {product.name}
                                                </Typography>
                                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 1 }}>
                                                    <Scale fontSize="small" />
                                                    <Typography variant="caption" color="text.secondary">
                                                        {unitName}
                                                    </Typography>
                                                </Box>
                                                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                                                    <Typography sx={{ fontWeight: 'bold' }}>
                                                        {formatCurrency(product.selling)}
                                                    </Typography>
                                                    <Chip
                                                        label={`Stock: ${product.stock} ${getUnitCode(product)}`}
                                                        size="small"
                                                        sx={{
                                                            backgroundColor: product.stock > 5 ? 'success.light' : 'warning.light',
                                                            color: 'white',
                                                            alignSelf: 'flex-start'
                                                        }}
                                                    />
                                                </Box>
                                            </CardContent>
                                        </Card>
                                    </Grid>
                                );
                            })}
                            {filteredProducts.length === 0 && (
                                <Grid item xs={12}>
                                    <Box sx={{ textAlign: 'center', py: 6, border: `1px dashed ${theme.palette.divider}`, borderRadius: 2 }}>
                                        <Search sx={{ fontSize: 48, color: 'text.secondary', mb: 2 }} />
                                        <Typography color="text.secondary" gutterBottom>No products found</Typography>
                                        <Typography variant="body2" color="text.secondary">
                                            {searchTerm || selectedCategory ? "Try adjusting your search or filter criteria" : "No products available in stock"}
                                        </Typography>
                                    </Box>
                                </Grid>
                            )}
                        </Grid>
                    </Paper>
                </Grid>

                {/* Order Summary - Right Side */}
                <Grid item xs={12} md={4} sx={{ flex: '0 0 50%', minWidth: 0 }}>
                    <Paper elevation={2} sx={{ p: 3, borderRadius: 3, height: '100%' }}>
                        <Typography variant="h5" sx={{ mb: 3, fontWeight: 'bold', color: 'primary.main' }}>
                            <ShoppingCart sx={{ verticalAlign: 'middle', mr: 1 }} />
                            Order Summary
                        </Typography>

                        <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 3 }}>
                            <Tabs value={orderSettingsTab} onChange={handleOrderSettingsTabChange} variant="fullWidth">
                                <Tab label="Customer Info" />
                                <Tab label="Order Settings" />
                                <Tab label="Cart & Payment" />
                            </Tabs>
                        </Box>

                        {/* Customer Info Tab */}
                        {orderSettingsTab === 0 && (
                            <Box>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                                    <Autocomplete
                                        fullWidth
                                        options={pageState.customersData}
                                        getOptionLabel={(option) => `${option.name} - ${option.phone}`}
                                        inputValue={customerSearchInput}
                                        onInputChange={(event, newInputValue) => {
                                            setCustomerSearchInput(newInputValue);
                                        }}
                                        renderInput={(params) => (
                                            <TextField 
                                                {...params} 
                                                label="Search Customer" 
                                                variant="outlined" 
                                                size="small"
                                                InputProps={{
                                                    ...params.InputProps,
                                                    startAdornment: (
                                                        <>
                                                            <InputAdornment position="start">
                                                                <Person />
                                                            </InputAdornment>
                                                            {params.InputProps.startAdornment}
                                                        </>
                                                    ),
                                                }}
                                            />
                                        )}
                                        onChange={(event, value) => handleCustomerSelect(value)}
                                        isOptionEqualToValue={(option, value) => option.id === value.id}
                                        renderOption={(props, option) => (
                                            <li {...props}>
                                                <Box>
                                                    <Typography variant="body1">{option.name}</Typography>
                                                    <Typography variant="caption" color="text.secondary">
                                                        Phone: {option.phone || 'N/A'}
                                                    </Typography>
                                                </Box>
                                            </li>
                                        )}
                                    />
                                    <Tooltip title="Create New Customer">
                                        <IconButton color="primary" onClick={handleCreateNewCustomer} size="small">
                                            <PersonAdd />
                                        </IconButton>
                                    </Tooltip>
                                </Box>
                                <Box sx={{ mb: 2 }}>
                                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                                        <Person color="action" sx={{ mr: 1 }} />
                                        <TextField 
                                            fullWidth 
                                            label="Customer Name" 
                                            variant="outlined" 
                                            value={formData.customerName} 
                                            name="customerName" 
                                            onChange={handleInputChange} 
                                            size="small" 
                                            required 
                                            disabled={!customerEditable} 
                                            InputProps={{
                                                startAdornment: (
                                                    <InputAdornment position="start">
                                                        <Person />
                                                    </InputAdornment>
                                                ),
                                            }}
                                        />
                                    </Box>
                                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                                        <Phone color="action" sx={{ mr: 1 }} />
                                        <TextField 
                                            fullWidth 
                                            label="Customer Phone" 
                                            variant="outlined" 
                                            value={formData.customerPhone} 
                                            name="customerPhone" 
                                            onChange={handleInputChange} 
                                            size="small" 
                                            disabled={!customerEditable}
                                            InputProps={{
                                                startAdornment: (
                                                    <InputAdornment position="start">
                                                        <Phone />
                                                    </InputAdornment>
                                                ),
                                            }}
                                        />
                                    </Box>
                                </Box>
                            </Box>
                        )}

                        {/* Order Settings Tab */}
                        {orderSettingsTab === 1 && (
                            <Box>
                                <FormControl fullWidth size="small" sx={{ mb: 2 }}>
                                    <InputLabel>Order Status</InputLabel>
                                    <Select value={formData.orderStatus} onChange={handleInputChange} name="orderStatus" label="Order Status">
                                        <MenuItem value="completed">Completed</MenuItem>
                                        <MenuItem value="pending">Pending</MenuItem>
                                        <MenuItem value="quotation">Quotation</MenuItem>
                                    </Select>
                                </FormControl>
                                <FormControl fullWidth size="small" sx={{ mb: 2 }}>
                                    <InputLabel>Payment Method</InputLabel>
                                    <Select value={formData.paymentMethod} onChange={handleInputChange} name="paymentMethod" label="Payment Method" disabled={isQuotation}>
                                        <MenuItem value="cash">Cash</MenuItem>
                                        <MenuItem value="mobile_money">Mobile Money</MenuItem>
                                        <MenuItem value="bank">Bank</MenuItem>
                                    </Select>
                                </FormControl>
                                <FormControl fullWidth size="small" sx={{ mb: 2 }}>
                                    <InputLabel>Order Type</InputLabel>
                                    <Select value={formData.orderType} onChange={handleInputChange} name="orderType" label="Order Type">
                                        <MenuItem value="direct_payment">Direct Payment</MenuItem>
                                        <MenuItem value="loan_order">Loan Order</MenuItem>
                                    </Select>
                                </FormControl>
                                <TextField 
                                    fullWidth 
                                    label="Order Notes (Optional)" 
                                    variant="outlined" 
                                    value={formData.orderNotes} 
                                    name="orderNotes" 
                                    onChange={handleInputChange} 
                                    multiline 
                                    rows={3} 
                                    size="small" 
                                />
                            </Box>
                        )}

                        {/* Cart & Payment Tab */}
                        {orderSettingsTab === 2 && (
                            <Box>
                                {/* Quotation Alert */}
                                {isQuotation && (
                                    <Alert severity="info" sx={{ mb: 2 }}>
                                        <Typography variant="body2">
                                            <strong>Quotation Mode:</strong> No payment required. This will create a quotation for the customer.
                                        </Typography>
                                    </Alert>
                                )}

                                {/* Cart Items */}
                                <Box sx={{ mb: 3 }}>
                                    <Typography sx={{ mb: 2, fontWeight: 'bold' }}>Cart Items ({cart.length})</Typography>
                                    <TableContainer sx={{ maxHeight: 250 }}>
                                        <Table size="small">
                                            <TableHead>
                                                <TableRow sx={{ backgroundColor: 'primary.main' }}>
                                                    <TableCell sx={{ color: 'white' }}>Item</TableCell>
                                                    <TableCell align="center" sx={{ color: 'white' }}>Qty</TableCell>
                                                    <TableCell align="right" sx={{ color: 'white' }}>Price</TableCell>
                                                    <TableCell align="right" sx={{ color: 'white' }}>Total</TableCell>
                                                    <TableCell align="center" sx={{ color: 'white' }}>Action</TableCell>
                                                </TableRow>
                                            </TableHead>
                                            <TableBody>
                                                {cart.map((item) => (
                                                    <TableRow key={item.id} hover>
                                                        <TableCell>
                                                            <Typography variant="body2" fontWeight="bold">{item.name}</Typography>
                                                            <Typography variant="caption" color="text.secondary">
                                                                {getCategoryName(item.category_id)}
                                                            </Typography>
                                                            <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                                                                <Scale fontSize="inherit" sx={{ fontSize: 10, mr: 0.5, verticalAlign: 'middle' }} />
                                                                {item.unit_name || 'N/A'}
                                                            </Typography>
                                                        </TableCell>
                                                        <TableCell align="center">
                                                            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                                                <IconButton size="small" onClick={() => adjustQuantity(item.id, -1)} disabled={item.quantity <= 1}>
                                                                    <Remove fontSize="small" />
                                                                </IconButton>
                                                                <Typography sx={{ mx: 1, minWidth: 20, textAlign: 'center' }}>{item.quantity}</Typography>
                                                                <IconButton size="small" onClick={() => adjustQuantity(item.id, 1)} disabled={item.quantity >= (pageState.productsData.find(p => p.id === item.id)?.stock || 0)}>
                                                                    <Add fontSize="small" />
                                                                </IconButton>
                                                            </Box>
                                                        </TableCell>
                                                        <TableCell align="right">
                                                            {editingPriceId === item.id ? (
                                                                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                                                    <Input
                                                                        type="number"
                                                                        value={editPriceValue}
                                                                        onChange={(e) => setEditPriceValue(e.target.value)}
                                                                        onKeyDown={(e) => handlePriceKeyDown(e, item.id)}
                                                                        autoFocus
                                                                        size="small"
                                                                        sx={{ width: 80 }}
                                                                        inputProps={{ min: 0, style: { textAlign: 'right' } }}
                                                                    />
                                                                    <IconButton size="small" onClick={() => saveEditedPrice(item.id)} color="success">
                                                                        <CheckCircle fontSize="small" />
                                                                    </IconButton>
                                                                    <IconButton size="small" onClick={cancelEditingPrice} color="error">
                                                                        <Cancel fontSize="small" />
                                                                    </IconButton>
                                                                </Box>
                                                            ) : (
                                                                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end' }}>
                                                                    <Typography variant="body2">
                                                                        {formatCurrency(item.price)}
                                                                        {item.price !== item.originalPrice && (
                                                                            <Typography component="span" variant="caption" sx={{ textDecoration: 'line-through', color: 'text.secondary', ml: 0.5 }}>
                                                                                {formatCurrency(item.originalPrice)}
                                                                            </Typography>
                                                                        )}
                                                                    </Typography>
                                                                    <IconButton size="small" onClick={() => startEditingPrice(item)} color="primary" sx={{ ml: 0.5 }}>
                                                                        <Edit fontSize="small" />
                                                                    </IconButton>
                                                                </Box>
                                                            )}
                                                        </TableCell>
                                                        <TableCell align="right">{formatCurrency(item.price * item.quantity)}</TableCell>
                                                        <TableCell align="center">
                                                            <IconButton size="small" onClick={() => removeFromCart(item.id)} color="error">
                                                                <Delete fontSize="small" />
                                                            </IconButton>
                                                        </TableCell>
                                                    </TableRow>
                                                ))}
                                            </TableBody>
                                        </Table>
                                    </TableContainer>
                                    {cart.length === 0 && (
                                        <Box sx={{ textAlign: 'center', py: 3 }}>
                                            <Typography variant="body2" color="textSecondary">Your cart is empty. Add products to continue.</Typography>
                                        </Box>
                                    )}
                                </Box>

                                <Divider sx={{ my: 2 }} />

                                {/* Payment Section - Only show for non-quotation orders */}
                                <Box sx={{ mb: 3 }}>
                                    <Box sx={{ backgroundColor: isQuotation ? 'info.light' : 'primary.light', p: 2, borderRadius: 2, mb: 2 }}>
                                        <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                                            <Typography sx={{ fontWeight: 'bold', color: 'white' }}>
                                                {isQuotation ? 'Quotation Total:' : 'Subtotal:'}
                                            </Typography>
                                            <Typography sx={{ fontWeight: 'bold', color: 'white' }}>{formatCurrency(calculateTotal())}</Typography>
                                        </Box>
                                    </Box>

                                    {/* Payment fields - Hidden for quotations */}
                                    {!isQuotation && formData.orderType === 'direct_payment' && (
                                        <>
                                            <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                                                <MoneyOffRounded color="action" sx={{ mr: 1 }} />
                                                <TextField 
                                                    fullWidth 
                                                    label="Customer Given Amount" 
                                                    variant="outlined" 
                                                    name="customerGivenCash" 
                                                    value={formData.customerGivenCash} 
                                                    onChange={handleInputChange} 
                                                    size="small" 
                                                    required 
                                                    InputProps={{
                                                        startAdornment: (
                                                            <InputAdornment position="start">
                                                                <MoneyOffRounded />
                                                            </InputAdornment>
                                                        ),
                                                    }}
                                                />
                                            </Box>
                                            <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                                <Money color="action" sx={{ mr: 1 }} />
                                                <TextField 
                                                    fullWidth 
                                                    label="Customer Balance" 
                                                    disabled 
                                                    variant="outlined" 
                                                    value={formatCurrency(calculateBalance())} 
                                                    size="small" 
                                                    sx={{ 
                                                        '& .MuiInputBase-input.Mui-disabled': { 
                                                            WebkitTextFillColor: calculateBalance() >= 0 ? 'green' : 'red', 
                                                            fontWeight: 'bold' 
                                                        } 
                                                    }} 
                                                />
                                            </Box>
                                        </>
                                    )}

                                    {/* Loan order note */}
                                    {!isQuotation && formData.orderType === 'loan_order' && (
                                        <Alert severity="warning" sx={{ mt: 2 }}>
                                            <Typography variant="body2">
                                                <strong>Loan Order:</strong> Customer will pay later. The remaining balance will be tracked.
                                            </Typography>
                                        </Alert>
                                    )}
                                </Box>
                            </Box>
                        )}

                        {/* Checkout Button */}
                        {hasPermission('ADD') && (
                            <Button 
                                fullWidth 
                                variant="contained" 
                                color={isQuotation ? "info" : "primary"} 
                                size="large" 
                                onClick={handleCheckout} 
                                disabled={cart.length === 0 || processingOrder} 
                                sx={{ mt: 1, py: 1.5, fontWeight: 'bold', fontSize: '1rem' }}
                            >
                                {processingOrder ? (
                                    <CircularProgress size={24} color="inherit" />
                                ) : isQuotation ? (
                                    'Create Quotation'
                                ) : (
                                    'Process Order'
                                )}
                            </Button>
                        )}

                        {!isQuotation && (
                            <PrintReceipt
                                orderData={{
                                    date: new Date().toISOString(),
                                    customerName: formData.customerName,
                                    customerPhone: formData.customerPhone,
                                    customerGivenCash: formData.customerGivenCash,
                                    customerBalance: formatCurrency(calculateBalance()),
                                    items: cart,
                                    total: calculateTotal(),
                                    orderType: formData.orderType,
                                    paymentMethod: formData.paymentMethod,
                                    orderStatus: formData.orderStatus
                                }}
                                disabled={cart.length === 0}
                                sx={{ mt: 1 }}
                            />
                        )}
                    </Paper>
                </Grid>
            </Grid>

            <Snackbar open={snackbar.open} autoHideDuration={6000} onClose={() => setSnackbar({ ...snackbar, open: false })}>
                <Alert severity={snackbar.severity} onClose={() => setSnackbar({ ...snackbar, open: false })}>{snackbar.message}</Alert>
            </Snackbar>
        </Container>
    );
};

export default Pos;